import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Box, Typography } from '@mui/material';
import { CopyIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { SearchCategory } from '../../../../UniversalSearch.constants';
import { getShortcutPrefix } from '../../../../UniversalSearch.utils';
import {
  copiedBadgeStyles,
  copyButtonStyles,
  fieldValueStyles,
  getResultItemIconStyles,
  getResultItemSelectedStyles,
  resultItemBreadcrumbStyles,
  resultItemContentStyles,
  resultItemDisabledStyles,
  resultItemLabelStyles,
  shortcutBadgeStyles,
} from '../../SearchResults.styles';

import { COPY_FEEDBACK_DURATION_MS, COPY_ICON_SIZE } from './SearchResultItem.constants';
import { CATEGORY_ICON_MAP, FieldIcon, RecordFieldIcon } from './SearchResultItem.icons';

const SearchResultItem = ({ item, isSelected = false, onClick, onMouseEnter }) => {
  const itemRef = useRef(null);
  const [isCopied, setIsCopied] = useState(false);
  const { isDisabled } = item;

  useEffect(() => {
    if (!isSelected || !itemRef.current) {
      return;
    }

    itemRef.current.scrollIntoView({ block: 'nearest' });
  }, [isSelected]);

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    onClick(item);
  }, [item, onClick, isDisabled]);

  const handleMouseEnter = useCallback(() => {
    onMouseEnter?.();
  }, [onMouseEnter]);

  const handleCopy = useCallback(
    event => {
      event.stopPropagation();
      navigator.clipboard.writeText(item.fieldValue);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), COPY_FEEDBACK_DURATION_MS);
    },
    [item.fieldValue],
  );

  const isRecordField = item.category === SearchCategory.RECORDS && item.isRecordField;
  const IconComponent = isRecordField
    ? RecordFieldIcon
    : CATEGORY_ICON_MAP[item.category] || FieldIcon;
  const selectedStyles = isDisabled
    ? resultItemDisabledStyles
    : getResultItemSelectedStyles({ isSelected });
  const iconStyles = getResultItemIconStyles({ isSelected: isSelected && !isDisabled });

  return (
    <Box ref={itemRef} sx={selectedStyles} onClick={handleClick} onMouseEnter={handleMouseEnter}>
      <Box sx={iconStyles}>
        <IconComponent />
      </Box>

      <Box sx={resultItemContentStyles}>
        <Typography sx={resultItemLabelStyles} variant="regular14">
          {item.label}
        </Typography>

        {item.breadcrumb && (
          <Typography sx={resultItemBreadcrumbStyles} variant="regular12">
            {item.breadcrumb}
          </Typography>
        )}
      </Box>

      {item.fieldValue && (
        <>
          <Typography sx={fieldValueStyles} variant="regular12">
            {item.fieldValue}
          </Typography>

          {isCopied ? (
            <Typography sx={copiedBadgeStyles} variant="regular12">
              Copied
            </Typography>
          ) : (
            <Box sx={copyButtonStyles} onClick={handleCopy}>
              <CopyIcon size={COPY_ICON_SIZE} />
            </Box>
          )}
        </>
      )}

      {item.shortcutKey && (
        <Typography sx={shortcutBadgeStyles} variant="medium12">
          {`${getShortcutPrefix()}${item.shortcutKey}`}
        </Typography>
      )}
    </Box>
  );
};

SearchResultItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    breadcrumb: PropTypes.string,
    fieldValue: PropTypes.string,
    isDisabled: PropTypes.bool,
    isRecordField: PropTypes.bool,
    shortcutKey: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool,
  onMouseEnter: PropTypes.func,
};

export default memo(SearchResultItem);
